import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { FileUploader } from '@/common/components/FileUploader'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import { checkIsPdf } from '@/common/utils/fileValidator'
import { FormTransformers } from '@/common/utils/formTransformers'
import { getFilenameFromUrl } from '@/common/utils/getFilenameFromUrl'
import { useUpdateContractEnvelopeSignatureType } from '@/features/privateCompanyLeadsFunnel/hooks/useUpdateContractEnvelopeSignatureType'
import { useUpdatePrivateCompanyLeadContractFile } from '@/features/privateCompanyLeadsFunnel/hooks/useUpdatePrivateCompanyLeadContractFile'
import { PrivateCompanyLeadData } from '@/features/privateCompanyLeadsFunnel/types/privateCompanyLead'
import { useQueryClient } from '@tanstack/react-query'
import { FileBadgeIcon } from 'lucide-react'
import { useState } from 'react'

interface ManualSignatureProps {
  privateCompanyLead: PrivateCompanyLeadData
  disableFields?: boolean
}

export const ManualSignature = ({ privateCompanyLead, disableFields }: ManualSignatureProps) => {
  const queryClient = useQueryClient()

  const [file, setFile] = useState<Blob>()
  const [fileName, setFileName] = useState('')

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['privateCompanyLead', privateCompanyLead.id] })
    queryClient.invalidateQueries({ queryKey: ['privateCompanyLeadContractEnvelope', privateCompanyLead.id] })
  }

  const { mutate: uploadContractFile } = useUpdatePrivateCompanyLeadContractFile({
    id: privateCompanyLead.id,
    onSuccess,
    onError: FormTransformers.errorsTransformer({ setMessages }),
  })

  const { mutate: updateSignatureType, isPending: updateSignatureTypeIsPending } =
    useUpdateContractEnvelopeSignatureType({
      id: privateCompanyLead.id,
      onSuccess,
      onError: FormTransformers.errorsTransformer({ setMessages }),
    })

  return (
    <Box className="flex flex-col gap-y-2 p-4">
      <div className="flex items-center justify-between">
        <p className="text-lg font-bold">Contrato (Assinatura manual)</p>

        <Button
          type="button"
          name="update_signature_type_clicksign"
          operationKlassToCheckPermission="Admin::Funnel::PrivateCompanyLeads::ContractEnvelope"
          onClick={() => updateSignatureType('clicksign')}
          className="flex items-center gap-x-2 bg-danger-op hover:bg-danger disabled:hover:bg-danger-op"
          disabled={updateSignatureTypeIsPending || disableFields}
          isLoading={updateSignatureTypeIsPending}
        >
          <p className="text-sm">Optar por assinatura via sistema</p>

          <FileBadgeIcon className="size-4" />
        </Button>
      </div>

      <FileUploader
        label=""
        title="Anexe o contrato assinado"
        onChange={(e) => {
          const file = (e.target as HTMLInputElement & { files: FileList }).files?.[0]
          if (!file || !checkIsPdf(file)) {
            return
          }
          if (file) {
            setFileName(file.name)
            setFile(file)
            uploadContractFile(file)
          }
        }}
        className="bg-yellow-op-2 border-none"
        name="document"
        accept="application/pdf"
        fileName={
          fileName
            ? fileName
            : privateCompanyLead?.contract?.file?.url
            ? getFilenameFromUrl(privateCompanyLead?.contract.file.url)
            : undefined
        }
        currentFileUrl={
          file
            ? URL.createObjectURL(file)
            : privateCompanyLead?.contract?.file?.url
            ? privateCompanyLead?.contract.file.url
            : undefined
        }
        disabled={disableFields}
      />

      <ModalMessageSimple messages={messages} setMessages={setMessages} isError />
    </Box>
  )
}
